/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2022, Zumper
 */

import { setListingDetailCookie } from '@zcode-ui/perm-address'
import { getDetailHref } from '@zcode-ui/web-zumper-global-state'
import { LinkOrButton, type LinkOrButtonProps } from '@zcode-ui/web-zumper-ui'
import { TopListable } from '@zcode/bizos'
import { keepBetaParam, locationDescriptorToString } from '@zcode/nr-utils'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import React, { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

export interface DetailPageLinkProps
  extends Pick<
    LinkOrButtonProps,
    'aria-hidden' | 'children' | 'className' | 'onClick' | 'tabIndex'
  > {
  entity: Listable | Pin<RawPinInventorySearch> | TopListable
}

export const DetailPageLink = ({ children, className, entity, onClick }: DetailPageLinkProps) => {
  const history = useHistory()
  const { isBuilding, isNoDetailPage, paUrl, paShouldIndex } = entity
  const isTopListable = entity instanceof TopListable
  const handleClick = useCallback(
    (e: Parameters<NonNullable<typeof onClick>>[0]) => {
      const href = locationDescriptorToString(keepBetaParam(getDetailHref(entity)), history)
      if (!isTopListable) {
        setListingDetailCookie(href, entity)
      }
      onClick?.(e)
    },
    [entity, history, onClick]
  )

  const maybeAllowLink =
    !isNoDetailPage && (isBuilding || paShouldIndex || (!paUrl && !entity.isShortTermFeed))

  return (
    <LinkOrButton
      className={twMerge(
        'DetailPageLink font-regular inline-block max-w-full no-underline text-gray-950 h-auto',
        className
      )}
      forceButton={!maybeAllowLink}
      onClick={handleClick}
      target="_blank"
      toEntity={entity}
      variant="link"
    >
      {children}
    </LinkOrButton>
  )
}
